<template>
    <div
        :class="[
            {
                'has-error': v && v.$anyError && type != 'ADDRESS',
                'form-search': type == 'SEARCH',
                'form-wysiwyg': type == 'WYSIWYG',
                'is-required': isRequired,
            },
            classes,
        ]"
        class="field"
        :disabled="iniDisabled">
        <label class="label" v-if="label">{{ label }}</label>
        <!--TEXT-->
        <div
            :class="{
                'field has-addons': hasAddons,
            }">
            <!--prepend-->
            <div class="control prepend" v-if="hasPrepend">
                <slot name="prepend" />
                <template v-if="type == 'BELGIANENTERPRISENUMBER'">
                    <a class="button is-static"> BE </a>
                </template>
            </div>

            <div class="control is-expanded">
                <template v-if="showDisabled">
                    <!--DISABLED VIEW-->
                    <template v-if="type == 'WYSIWYG'">
                        <div
                            :class="[
                                showCondensed ? 'condensed' : '',
                                !value ? 'empty' : 'wysiwyg',
                                hasScrollBar ? 'has-scroll' : '',
                            ]"
                            v-html="value"
                            ref="wysiwyg"></div>
                    </template>
                    <template v-else-if="type == 'PASSWORD'">
                        <div class="control is-clearfix">
                            <input
                                ref="input"
                                class="input"
                                type="password"
                                autocomplete="off"
                                :value="selected"
                                disabled="disabled" />
                        </div>
                    </template>
                    <template v-else>
                        <div class="input disabled">
                            <template v-if="type == 'EMAIL'">
                                <a :href="`mailto:${selected}`" class="txt">{{ selected }}</a>
                            </template>
                            <template v-else-if="type == 'URI'">
                                <a :href="`${selected}`" class="txt">{{ selected }}</a>
                            </template>
                            <template v-else-if="type == 'PHONE'">
                                <a :href="`tel:${selected}`" class="txt">{{ selected }}</a>
                            </template>
                            <template v-else>
                                {{ selected }}
                            </template>
                        </div>
                    </template>
                </template>
                <template v-else>
                    <!--EDIT VIEW-->
                    <template v-if="type == 'TEXT'">
                        <div class="control is-clearfix" :class="{ 'has-icons-left': !!this.$slots.icon }">
                            <span class="icon is-left" v-if="!!this.$slots.icon">
                                <slot name="icon" />
                            </span>
                            <input
                                ref="input"
                                class="input"
                                type="text"
                                autocomplete="on"
                                :value="selected"
                                :placeholder="placeholder"
                                @input="onChange"
                                @focus="
                                    (event) => {
                                        $emit('focus', event);
                                    }
                                "
                                @blur="
                                    (event) => {
                                        $emit('blur', event);
                                    }
                                "
                                v-on:keyup="
                                    (event) => {
                                        $emit('keyup', event);
                                    }
                                " />
                        </div>
                    </template>

                    <!--SEARCH-->
                    <template v-if="type == 'SEARCH'">
                        <div class="control is-expanded">
                            <div class="control search has-icons-left is-clearfix has-icons-right">
                                <input
                                    type="search"
                                    autocomplete="on"
                                    :placeholder="$t('search')"
                                    class="input"
                                    :value="selected"
                                    @input="onChange" />
                                <span class="icon is-left">
                                    <font-awesome-icon :icon="['fas', 'magnifying-glass']" />
                                </span>
                                <span
                                    class="icon is-right is-clickable"
                                    @click.prevent="onClearSearch"
                                    v-if="!iniDisabled && selected">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </span>
                            </div>
                        </div>
                    </template>

                    <!--PASSWORD-->
                    <template v-else-if="type == 'PASSWORD'">
                        <div class="control is-expanded">
                            <div class="control search has-icons-left is-clearfix">
                                <input
                                    type="password"
                                    autocomplete="off"
                                    class="input"
                                    :value="selected"
                                    @input="onChange" />
                                <span class="icon is-left">
                                    <font-awesome-icon :icon="['fas', 'lock']" />
                                </span>
                            </div>
                        </div>
                    </template>

                    <!--NUMBER-->
                    <template v-else-if="type == 'NUMBER'">
                        <div class="control is-expanded">
                            <input
                                type="number"
                                step="any"
                                class="input"
                                :min="min"
                                :max="max"
                                :value="selected"
                                @input="onChange" />
                        </div>
                    </template>

                    <!--EMAIL-->
                    <template v-else-if="type == 'EMAIL'">
                        <div class="control is-clearfix has-icons-left">
                            <input type="email" autocomplete="on" class="input" :value="selected" @input="onChange" />
                            <span class="icon is-left">
                                <font-awesome-icon :icon="['far', 'envelope-open']" />
                            </span>
                        </div>
                    </template>

                    <!--URI-->
                    <template v-else-if="type == 'URI'">
                        <div class="control is-clearfix has-icons-left">
                            <input type="url" autocomplete="on" class="input" :value="selected" @input="onChange" />
                            <span class="icon is-left">
                                <font-awesome-icon :icon="['fas', 'globe']" />
                            </span>
                        </div>
                    </template>

                    <!--WYSIWYG-->
                    <template v-else-if="type == 'WYSIWYG'">
                        <wysiwyg :disabled="iniDisabled" v-model="selected" @input="onChangeWysiwyg"></wysiwyg>
                    </template>

                    <!--CUSTOM-->
                    <template v-else-if="type == 'CUSTOM'">
                        <slot name="custom" />
                    </template>

                    <!--ADDRESS-->
                    <template v-else-if="type == 'ADDRESS'">
                        <addressForm
                            :iniDisabled="iniDisabled"
                            :v="v"
                            :hideActions="true"
                            :hideMessages="true"
                            :hideChanges="true"
                            :iniData="selected"
                            v-model="selected"
                            :isCollapsed="isCollapsed"
                            :usePopover="showCondensed"
                            @input="onAddressChange"
                            :autoComplete="autoComplete"
                            @placeSelected="
                                (value) => {
                                    $emit('placeSelected', value);
                                }
                            " />
                    </template>

                    <!--PHONE-->
                    <template v-else-if="type == 'PHONE'">
                        <inputPhone
                            v-model="selected"
                            @input="onChange"
                            :v="v"
                            :iniDisabled="iniDisabled"
                            :country="country" />
                    </template>

                    <!--INSS-->
                    <template v-else-if="type == 'INSS'">
                        <div class="control is-clearfix" :class="{ 'has-icons-left': !iniDisabled }">
                            <span class="icon is-left" v-if="!iniDisabled">
                                <font-awesome-icon :icon="['fas', 'address-card']" />
                            </span>
                            <input
                                type="text"
                                v-mask="maskNationalnumber"
                                v-model="selected"
                                class="input"
                                :disabled="iniDisabled"
                                @input="onChange" />
                        </div>
                    </template>
                    <!--INSS : user case -->
                    <template v-else-if="type == 'BE_NO_PREFIX_INSS'">
                        <div class="control is-clearfix" :class="{ 'has-icons-left': !iniDisabled }">
                            <span class="icon is-left" v-if="!iniDisabled">
                                <font-awesome-icon :icon="['fas', 'address-card']" />
                            </span>
                            <input
                                type="text"
                                v-mask="masBEkNationalnumberNoPrefix"
                                v-model="selected"
                                class="input"
                                :disabled="iniDisabled"
                                @input="onChange" />
                        </div>
                    </template>
                    <!--BELGIAN ENTERPRISE NUMBER-->
                    <template v-else-if="type == 'BELGIANENTERPRISENUMBER'">
                        <input
                            type="text"
                            v-mask="maskBelgianEnterprisenumber"
                            v-model="selected"
                            class="input"
                            :disabled="iniDisabled"
                            @input="onChange" />
                    </template>
                    <!--IBAN-->
                    <template v-else-if="type == 'IBAN'">
                        <div class="control is-clearfix" :class="{ 'has-icons-left': !iniDisabled }">
                            <span class="icon is-left" v-if="!iniDisabled">
                                <font-awesome-icon :icon="['fas', 'university']" />
                            </span>
                            <input
                                type="text"
                                v-mask="maskIbanNumber"
                                v-model="selected"
                                class="input"
                                :disabled="iniDisabled"
                                @input="onChange" />
                        </div>
                    </template>
                </template>
            </div>

            <!--append-->
            <div class="control append" v-if="!!this.$slots.append">
                <slot name="append" />
            </div>
        </div>

        <span v-if="v && v.$anyError" class="help is-danger">
            <slot name="onError" />
        </span>
        <span class="help info" v-if="!!this.$slots.help">
            <slot name="help" />
        </span>
    </div>
</template>

<script>
import inputMasks from './../mixins/inputMasks.js';
const wysiwyg = () => import('./../components/FormWysiwyg.vue');
const addressForm = () => import('./../components/AddressForm.vue');
const inputPhone = () => import('./../components/PhoneForm.vue');
import { isValid_onlyBelgianINSS } from './../mixins/inputValidations.js';

import { library } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    faMagnifyingGlass,
    faTimes,
    faLock,
    faGlobe,
    faAddressCard,
    faUniversity,
} from '@fortawesome/free-solid-svg-icons';
import { faEnvelopeOpen } from '@fortawesome/free-regular-svg-icons';

library.add(faMagnifyingGlass);
library.add(faTimes);
library.add(faLock);
library.add(faGlobe);
library.add(faAddressCard);
library.add(faUniversity);
library.add(faEnvelopeOpen);

export default {
    name: 'textInput',
    components: {
        wysiwyg,
        addressForm,
        inputPhone,
        FontAwesomeIcon,
    },
    props: {
        id: { type: String },
        type: { type: String, default: 'TEXT' },
        v: [Object, Function],
        label: String,
        iniDisabled: { type: Boolean, default: false },
        value: [String, Object, Number],
        placeholder: String,
        classes: String,
        showCondensed: Boolean,
        min: String,
        max: String,
        autoComplete: { type: Boolean, default: false },
        debounced: { type: Boolean, default: false },
        debounceInterval: { type: Number, default: 700 },
        country: { type: String },
        isCollapsed: { type: Boolean, default: true },
    },
    mixins: [inputMasks],
    data() {
        return {
            selected: null,
            hasScrollBar: false,
            timeoutID: null, //manual debounce
        };
    },
    computed: {
        _debounceInterval() {
            if (this.debounced || this.type == 'SEARCH') {
                return this.debounceInterval;
            }
            return 0;
        },
        hasAddons() {
            return !!this.$slots.append || !!this.$slots.prepend || this.type == 'BELGIANENTERPRISENUMBER';
        },
        hasPrepend() {
            return !!this.$slots.prepend || this.type == 'BELGIANENTERPRISENUMBER';
        },
        showDisabled() {
            //keep the default component for address when disabled
            if (
                this.iniDisabled &&
                this.type != 'ADDRESS' &&
                this.type != 'INSS' &&
                this.type != 'BE_NO_PREFIX_INSS' &&
                this.type != 'IBAN' &&
                this.type != 'BELGIANENTERPRISENUMBER' &&
                this.type != 'CUSTOM'
            ) {
                return true;
            }
            return false;
        },
        isRequired() {
            return this.v && this.v.required === false;
        },
    },
    watch: {
        value() {
            this.handleValue();
        },
    },
    created() {
        this.handleValue();
    },
    mounted() {
        this.checkVerticalScrollBar();
    },
    methods: {
        handleValue() {
            this.selected = this.value;
            if (!this.iniDisabled && this.type == 'BE_NO_PREFIX_INSS') {
                this.extractBelgiumInss(this.value);
            }
        },
        onAddressChange(value) {
            this.$emit('input', value);
        },
        onClearSearch() {
            this.$emit('input', null);
        },
        onChangeWysiwyg(value) {
            this.$emit('input', value);
        },
        onChange(event) {
            var value = event.target.value;
            //manual form of debouncing
            clearTimeout(this.timeoutID);
            this.timeoutID = setTimeout(() => {
                if (this.type == 'TEXT') {
                    //value = value.trim();
                }
                this.selected = value;

                if (this.type == 'BELGIANENTERPRISENUMBER') {
                    this.$emit('input', 'BE' + value);
                } else {
                    this.$emit('input', value);
                }
            }, this._debounceInterval);
        },
        checkVerticalScrollBar() {
            this.hasScrollBar = this.$refs.wysiwyg?.clientHeight < this.$refs.wysiwyg?.scrollHeight;
        },
        extractBelgiumInss(inss) {
            if (isValid_onlyBelgianINSS(inss)) {
                var number = inss;

                const mod97 = (value) => {
                    let m = value % 97;
                    m = Math.abs(m - 97);
                    if (m === 0) m = 97;

                    return m;
                };

                if (!number) {
                    return;
                } else {
                    var is2K = false;
                    var checkDigit = number.substr(9, 2);
                    var nrToCheck = number.substr(0, 9);

                    if (mod97('2' + nrToCheck) == checkDigit) {
                        is2K = true;
                    }

                    //The first six digits are the birth day in reversed order
                    let year = Number(number.slice(0, 2));
                    let month = Number(number.slice(2, 4));
                    let day = Number(number.slice(4, 6));
                    let genderKnown = true;

                    //Check for Bisnummer:
                    // for persons with no inss number:
                    // month is raised by 40 if gender is know
                    // raised by 20 if gender is not know.
                    if (month > 12 && month < 12 + 20) {
                        genderKnown = false;
                        month -= 20;
                    } else if (month > 12 + 20 && month < 12 + 40) {
                        month -= 40;
                    }

                    //Born after/before 2000
                    if (is2K) {
                        year += 2000;
                    } else {
                        year += 1900;
                    }
                    this.$emit('birthdate', new Date(year, month - 1, day));

                    //The following 3 digits: when even gender is female, when uneven gender is male.
                    var sex = 'OTHER';
                    if (genderKnown) {
                        sex = Number(number.slice(6, 9)) % 2 == 0 ? 'FEMALE' : 'MALE';
                    }
                    this.$emit('gender', sex);
                }
            } else {
                return;
            }
        },
    },
    i18n: {
        messages: {
            en: {
                search: 'Search',
            },
            fr: {
                search: 'Recherche',
            },
            nl: {
                search: 'Zoeken',
            },
        },
    },
};
</script>
